import type { HttpClient } from '../client';
import type {
  MessageTplRow,
  MessageTplSaveRequest,
  MetricDefRow,
  MetricDefSaveRequest,
  PlatformParamRow,
  PlatformParamSaveRequest,
  TplKind,
  TplRow,
  TplSaveRequest
} from '../types/platform';

const TPL = '/sys/tpl';
const METRIC = '/sys/metric-def';
const MESSAGE = '/sys/message-tpl';
const PARAM = '/sys/param';

/** 新增/编辑统一返回：`created` 表示新建（含复活历史软删行），`updated` 表示更新。 */
export interface PlatformSaveResult {
  id: string;
  created?: boolean;
  updated?: boolean;
  revived?: boolean;
}

export interface PlatformDeleteResult {
  id: string;
  deleted: boolean;
}

/**
 * 平台基础数据（web-system `/platform`）：
 * 角色/审批流模板（`sys_tpl`）、指标定义（`cfg_metric_def`）、消息模板（`cfg_message_tpl`）、
 * 平台参数（`sys_param`，tenant_id=0）。均要求 `portal=SYSTEM` + `sys:platform:config`。
 */
export function createPlatformApi(http: HttpClient) {
  return {
    /** 角色模板 / 审批流模板。 */
    tpl: {
      list(kind: TplKind): Promise<TplRow[]> {
        return http.request<TplRow[]>({ path: `${TPL}/${kind}` });
      },
      save(body: TplSaveRequest): Promise<PlatformSaveResult> {
        return http.request<PlatformSaveResult>({ path: TPL, method: 'POST', body });
      },
      remove(id: string): Promise<PlatformDeleteResult> {
        return http.request<PlatformDeleteResult>({ path: `${TPL}/${id}`, method: 'DELETE' });
      }
    },

    /** 监管指标口径定义。 */
    metricDef: {
      list(): Promise<MetricDefRow[]> {
        return http.request<MetricDefRow[]>({ path: METRIC });
      },
      save(body: MetricDefSaveRequest): Promise<PlatformSaveResult> {
        return http.request<PlatformSaveResult>({ path: METRIC, method: 'POST', body });
      },
      remove(id: string): Promise<PlatformDeleteResult> {
        return http.request<PlatformDeleteResult>({ path: `${METRIC}/${id}`, method: 'DELETE' });
      }
    },

    /** 消息模板母版。 */
    messageTpl: {
      list(): Promise<MessageTplRow[]> {
        return http.request<MessageTplRow[]>({ path: MESSAGE });
      },
      save(body: MessageTplSaveRequest): Promise<PlatformSaveResult> {
        return http.request<PlatformSaveResult>({ path: MESSAGE, method: 'POST', body });
      },
      remove(id: string): Promise<PlatformDeleteResult> {
        return http.request<PlatformDeleteResult>({ path: `${MESSAGE}/${id}`, method: 'DELETE' });
      }
    },

    /** 平台参数（tenant_id=0）。 */
    param: {
      list(): Promise<PlatformParamRow[]> {
        return http.request<PlatformParamRow[]>({ path: PARAM });
      },
      save(body: PlatformParamSaveRequest): Promise<PlatformSaveResult> {
        return http.request<PlatformSaveResult>({ path: PARAM, method: 'POST', body });
      },
      remove(id: string): Promise<PlatformDeleteResult> {
        return http.request<PlatformDeleteResult>({ path: `${PARAM}/${id}`, method: 'DELETE' });
      }
    }
  };
}

export type PlatformApi = ReturnType<typeof createPlatformApi>;
